import React from "react";
import {
  BookBookmark,
  CaretDoubleLeft,
  CaretDoubleRight,
  ChatTeardropText,
  Command,
  Gauge,
  MagnifyingGlass,
  SignOut,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { plansPath } from "@hypertune/shared-internal";
import SidebarItem, { SidebarItemProps } from "./SidebarItem";
import {
  docsUrl,
  intentDangerHex,
  intentWarningHex,
  lighterGreyHex,
} from "../lib/constants";
import useStickyState from "../lib/hooks/useStickyState";
import Button from "./buttons/Button";
import ShortcutDefinition from "./ShortcutDefinition";
import useIntercom from "../app/useIntercom";
import Tooltip from "./tooltips/Tooltip";
import { useHypertune } from "../generated/hypertune.react";
import {
  PrimaryBusinessUsageQuery,
  usePrimaryBusinessUsageQuery,
} from "../generated/graphql";
import { getCookieConsent } from "../lib/cookieConsent";
import { RootNode } from "../generated/hypertune";
import { Intent } from "./intent";
import getPercentage from "../lib/generic/getPercentage";
import useSignOut from "../lib/hooks/useSignOut";

export type Section = {
  items: SidebarItemProps[];
};

export default function Sidebar({
  sidebarId,
  collapsible,
  setIsCommandPaletteVisible,
  showCommandPaletteTooltip,
  sections,
  bottomItems,
}: {
  sidebarId: string;
  collapsible?: boolean;
  setIsCommandPaletteVisible?: (isVisible: boolean) => void;
  showCommandPaletteTooltip?: boolean;
  sections: Section[];
  bottomItems?: SidebarItemProps[];
}): React.ReactElement {
  const hypertune = useHypertune();
  const hasCookieConsent = getCookieConsent();

  const content = hypertune.content();
  const { data, loading } = usePrimaryBusinessUsageQuery();

  const signOut = useSignOut();

  const { showIntercom } = useIntercom();
  const [isCollapsed, setIsCollapsed] = useStickyState(
    false,
    `${sidebarId}-collapsed`
  );

  return (
    <div
      className={`flex flex-shrink-0 flex-col items-start gap-[7px] overflow-y-auto overflow-x-hidden border-r p-[10px] ${isCollapsed ? "w-[53px]" : "w-[248px]"}`}
    >
      {collapsible && (
        <div
          className={`flex ${
            isCollapsed ? "flex-col gap-2" : "flex-row gap-[6px]"
          } mb-[9px] w-full`}
        >
          <Tooltip
            id="logic"
            step={2}
            allSteps={content.logic().tooltips()}
            isVisible={showCommandPaletteTooltip}
            className={!isCollapsed ? "flex w-full flex-row" : ""}
          >
            <Button
              size="large"
              weight="elevated"
              icon={
                <MagnifyingGlass
                  size={14}
                  weight="regular"
                  color={lighterGreyHex}
                />
              }
              text={!isCollapsed ? "Search..." : undefined}
              iconEnd={
                !isCollapsed ? (
                  <ShortcutDefinition
                    keys={[
                      <>
                        <Command
                          size={12}
                          weight="regular"
                          color={lighterGreyHex}
                        />
                        K
                      </>,
                    ]}
                  />
                ) : undefined
              }
              onClick={
                setIsCommandPaletteVisible
                  ? () => setIsCommandPaletteVisible(true)
                  : undefined
              }
              className={!isCollapsed ? "flex-grow justify-between" : ""}
            />
          </Tooltip>
          <Button
            size="large"
            weight="elevated"
            icon={
              isCollapsed ? (
                <CaretDoubleRight
                  size={14}
                  weight="regular"
                  color={lighterGreyHex}
                />
              ) : (
                <CaretDoubleLeft
                  size={14}
                  weight="regular"
                  color={lighterGreyHex}
                />
              )
            }
            onClick={() => setIsCollapsed((collapsed) => !collapsed)}
          />
        </div>
      )}
      {sections.map(
        (section) =>
          section.items.map(
            ({
              icon,
              title,
              onClick,
              link,
              hasError,
              isSelected,
              children,
            }) => (
              <SidebarItem
                key={title}
                isCollapsed={isCollapsed}
                icon={icon}
                title={title}
                onClick={onClick}
                link={link}
                hasError={hasError}
                isSelected={isSelected}
              >
                {children}
              </SidebarItem>
            )
          )
        // TODO: add divider if there is more than one section.
      )}
      <div className="mt-auto min-h-[68px] w-full" />
      <div className="flex w-full flex-col items-stretch gap-[7px]">
        {!loading &&
          data?.primaryBusiness &&
          data.primaryBusiness.business.plan === "free" && (
            <OverageMessage
              isCollapsed={isCollapsed}
              primaryBusiness={data?.primaryBusiness}
            />
          )}
        {bottomItems?.map(
          ({ icon, title, onClick, link, hasError, isSelected, children }) => (
            <SidebarItem
              key={title}
              isCollapsed={isCollapsed}
              icon={icon}
              title={title}
              onClick={onClick}
              link={link}
              hasError={hasError}
              isSelected={isSelected}
            >
              {children}
            </SidebarItem>
          )
        ) || null}
        <SidebarItem
          isCollapsed={isCollapsed}
          icon={<ChatTeardropText />}
          title="Live chat"
          onClick={showIntercom}
          loading={showIntercom.loading}
        />
        <SidebarItem
          isCollapsed={isCollapsed}
          icon={<BookBookmark />}
          title="Documentation"
          link={{ href: docsUrl, isExternal: true }}
        />
        <SidebarItem
          isCollapsed={isCollapsed}
          icon={<SignOut />}
          title="Sign out"
          onClick={signOut}
          className={
            !hasCookieConsent ? "mb-[128.5px] 2xs:mb-[69px] xs:mb-[59.5px]" : ""
          }
        />
      </div>
    </div>
  );
}

function OverageMessage({
  isCollapsed,
  primaryBusiness,
}: {
  isCollapsed: boolean;
  primaryBusiness: NonNullable<PrimaryBusinessUsageQuery["primaryBusiness"]>;
}): React.ReactElement | null {
  const navigate = useNavigate();
  const hypertune = useHypertune();
  const limits = hypertune.features().planLimits().get();

  const teamMemberOverageIntent: Intent | null =
    limits.teamSize > 0 &&
    primaryBusiness.business.memberCount > limits.teamSize
      ? "danger"
      : null;

  const teamMemberOverageDescription = teamMemberOverageIntent
    ? `${primaryBusiness.business.memberCount}/${limits.teamSize} team members`
    : null;

  const billedRequestOverageIntent = getUsageIntent(
    hypertune,
    primaryBusiness.business.billedRequestCurrentMonthUsage,
    limits.billedRequestCount
  );

  const billedRequestOverageDescription = billedRequestOverageIntent
    ? `${getPercentage(
        primaryBusiness.business.billedRequestCurrentMonthUsage,
        limits.billedRequestCount
      )} of requests`
    : null;

  const billedEventOverageIntent = getUsageIntent(
    hypertune,
    primaryBusiness.business.billedEventCurrentMonthUsage,
    limits.analyticsEventsCount
  );

  const billedEventOverageDescription = billedEventOverageIntent
    ? `${getPercentage(
        primaryBusiness.business.billedEventCurrentMonthUsage,
        limits.analyticsEventsCount
      )} of events`
    : null;

  const intents = [
    teamMemberOverageIntent,
    billedRequestOverageIntent,
    billedEventOverageIntent,
  ];

  if (!intents.some((i) => i)) {
    return null;
  }

  const intent = intents.some((i) => i === "danger") ? "danger" : "warning";

  const title =
    intent === "danger" ? "Usage limits exceeded" : "Approaching usage limits";

  const descriptions = [
    billedRequestOverageDescription,
    billedEventOverageDescription,
    teamMemberOverageDescription,
  ].filter(Boolean);

  const description =
    descriptions.length === 0
      ? ""
      : `Used ${
          descriptions.length === 1
            ? descriptions[0]
            : descriptions.length === 2
              ? descriptions.join(" and ")
              : `${descriptions.slice(0, -1).join(", ")}, and ${
                  descriptions[descriptions.length - 1]
                }`
        }.`;

  return (
    <SidebarItem
      isCollapsed={isCollapsed}
      icon={
        <Gauge
          color={intent === "danger" ? intentDangerHex : intentWarningHex}
          weight="fill"
        />
      }
      title={title}
      subtitle={description}
      intent={intent}
      variant="minimal"
      onClick={() => navigate(plansPath)}
    />
  );
}

function getUsageIntent(
  hypertune: RootNode,
  usage: number,
  limit: number
): Intent | null {
  if (limit <= 0) {
    return null;
  }

  if (usage > limit) {
    return "danger";
  }

  if (
    usage / limit >=
    hypertune.features().earlyWarningThresholdPercentage({ fallback: 0.5 })
  ) {
    return "warning";
  }

  return null;
}
